import { isAreaFeature, type EditableFloorPlan, type EditorFeature } from '../models/editor-feature';
import { isKnownMdiIcon } from './mdi-icons';
import { editableFeaturesOf, selectionKeyOf, type SelectionKey } from './selection';
import { areaMessageSuffixOf } from './drawable-geometry';

/**
 * Checks a floorplan before it is saved.
 *
 * The server enforces all of this too, and it is the real boundary — this pass exists so
 * the admin gets a list of offending features they can click through instead of one opaque
 * "save failed", and so a mistake is caught before a round-trip.
 *
 * What it looks for is what actually breaks a consumer downstream: a missing identifier or
 * a duplicate one leaves a feature permanently unreachable, and an icon name outside the
 * app's MDI major renders as nothing at all, silently.
 */

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  readonly severity: ValidationSeverity;
  readonly key: SelectionKey;
  /** A translation key, so the message itself stays out of this layer. */
  readonly messageKey: string;
  readonly context?: string;
}

export interface ValidationResult {
  readonly issues: ValidationIssue[];
  /** Errors block a save; warnings do not. */
  readonly blocking: boolean;
}

const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

const identifierIssues = (feature: EditorFeature): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  if (!isNonEmptyString(feature.id) || !isNonEmptyString(feature.featureId)) {
    issues.push({
      severity: 'error',
      key: selectionKeyOf(feature),
      messageKey: 'floorplan-editor-validation-missing-identifier',
    });
  }

  return issues;
};

const geometryIssues = (feature: EditorFeature): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Every feature whose coordinates are a MultiPolygon, not just desk shapes. Testing the kind
  // rather than the shape is what made this read a wall's rings as an `[x, y]` pair the moment
  // drawn areas became selectable — 1,202 features on one real plan reported as having an
  // invalid position, which would have blocked saving the plan outright.
  if (isAreaFeature(feature)) {
    // Three vertices, not four. Four is a *closed* triangle, and the stored data is full of
    // rings that are not closed — BDO Eindhoven's floor layer alone has twelve. The server
    // closes a ring on the way in, so demanding it here would block saving a plan over
    // geometry the editor never drew and cannot fix, which is the same trap duplicate
    // featureIds set once already.
    const hasVertices = feature.coordinates.every(
      (polygon) => polygon.length > 0 && polygon.every((ring) => ring.length >= 3),
    );

    if (!feature.coordinates.length || !hasVertices) {
      issues.push({
        severity: 'error',
        key: selectionKeyOf(feature),
        // Named for what it is. This used to say "a desk shape" for every outline, so a broken
        // room zone or wall was reported as a desk nobody could find.
        messageKey: `floorplan-editor-validation-invalid-shape-${areaMessageSuffixOf(feature)}`,
      });
    }

    return issues;
  }

  const [x, y] = feature.coordinates;
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    issues.push({
      severity: 'error',
      key: selectionKeyOf(feature),
      messageKey: 'floorplan-editor-validation-invalid-position',
    });
  }

  return issues;
};

const contentIssues = (feature: EditorFeature): ValidationIssue[] => {
  if (feature.kind === 'textLabel' && !feature.text.trim()) {
    // Not an error: an empty annotation is harmless, it simply draws nothing. Worth saying
    // out loud, because on the plan it looks identical to one that failed to render.
    return [{
      severity: 'warning',
      key: selectionKeyOf(feature),
      messageKey: 'floorplan-editor-validation-empty-text',
    }];
  }

  if (feature.kind === 'desk' && !feature.roomId) {
    // Not an error, because the stored data is full of them: 35 desk features have no
    // workplace behind them and 113 name one belonging to another office. Saving has to
    // stay possible, but a desk nobody can book looks exactly like one that works.
    return [{
      severity: 'warning',
      key: selectionKeyOf(feature),
      messageKey: 'floorplan-editor-validation-desk-without-room',
    }];
  }

  if (feature.kind === 'iconLabel' && !isKnownMdiIcon(feature.iconName)) {
    // The mobile app draws nothing for a name it does not recognise, with no error
    // anywhere, so this warning is the only signal the admin will ever get.
    return [{
      severity: 'warning',
      key: selectionKeyOf(feature),
      messageKey: 'floorplan-editor-validation-unknown-icon',
      context: feature.iconName ?? '',
    }];
  }

  return [];
};

/**
 * Duplicate feature ids, per layer, reported but never blocking.
 *
 * A duplicate is a real defect — consumers find a feature by first match on `featureId`, so
 * the second one becomes permanently unreachable — but it is a defect the editor mostly did
 * not create. Two floorplans in the database already have duplicate desk ids and three have
 * duplicate annotation ids, all predating this feature. Refusing to save those plans would
 * lock an admin out of fixing anything else on them, which is worse than the duplicate.
 *
 * Desk shapes are not checked at all: 3,290 floor features across 34 plans share the zero
 * UUID, because nothing has ever looked a floor feature up by its id. Flagging those would
 * bury every real warning under hundreds of false ones.
 */
const duplicateIssues = (features: EditorFeature[]): ValidationIssue[] => {
  const seen = new Set<string>();
  const issues: ValidationIssue[] = [];

  features.forEach((feature) => {
    if (feature.kind === 'deskShape') {
      return;
    }

    if (seen.has(feature.featureId)) {
      issues.push({
        severity: 'warning',
        key: selectionKeyOf(feature),
        messageKey: 'floorplan-editor-validation-duplicate-identifier',
        context: feature.featureId,
      });
      return;
    }

    seen.add(feature.featureId);
  });

  return issues;
};

export const validateFloorPlan = (floorPlan: EditableFloorPlan): ValidationResult => {
  const features = editableFeaturesOf(floorPlan);

  const issues = [
    ...features.flatMap(identifierIssues),
    ...features.flatMap(geometryIssues),
    ...features.flatMap(contentIssues),
    ...duplicateIssues(floorPlan.desks),
    ...duplicateIssues(floorPlan.labels),
    ...duplicateIssues(floorPlan.deskShapes),
  ];

  return { issues, blocking: issues.some((issue) => issue.severity === 'error') };
};

export default validateFloorPlan;
